import React, { useState, useMemo } from "react";
import Papa from "papaparse";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import { CloudUpload } from "@material-ui/icons";

import { parse, parseISO, isValid, formatISO } from "date-fns";

const DATE_FORMATS = [
  "yyyy-MM-dd",
  "dd-MM-yyyy",
  "MM-dd-yyyy",
  "dd/MM/yyyy",
  "MM/dd/yyyy",
];

const normalizeDateToISO = (value) => {
  if (!value) return null;

  // Case 1: already a Date object
  if (value instanceof Date && isValid(value)) {
    return formatISO(value, { representation: "date" });
  }

  // Case 2: ISO string
  if (typeof value === "string") {
    const isoParsed = parseISO(value);
    if (isValid(isoParsed)) {
      return formatISO(isoParsed, { representation: "date" });
    }

    // Try known CSV formats
    for (const fmt of DATE_FORMATS) {
      const parsed = parse(value, fmt, new Date());
      if (isValid(parsed)) {
        return formatISO(parsed, { representation: "date" });
      }
    }
  }

  // Case 3: Excel serial number
  if (typeof value === "number") {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const parsed = new Date(excelEpoch.getTime() + value * 86400000);
    if (isValid(parsed)) {
      return formatISO(parsed, { representation: "date" });
    }
  }

  return null;
};


const SAMPLE_ROWS = [
  { Date: "2026-01-01", SAMPLE_INDEX: 100 },
  { Date: "2026-01-02", SAMPLE_INDEX: 101.5 },
  { Date: "2026-01-03", SAMPLE_INDEX: 99.8 },
];

const CSVUploadModal = ({ onUpload, vesselIndexesData = [] }) => {
  const [file, setFile] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [openDemoInfo, setOpenDemoInfo] = useState(false);

  /**
   * Collect all unique index names
   */
  const indexNames = useMemo(() => {
    const set = new Set();
    vesselIndexesData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== "_id" && key !== "Date") {
          set.add(key);
        }
      });
    });
    return Array.from(set);
  }, [vesselIndexesData]);

  /**
   * Build multi-column demo CSV
   */
  const handleDownloadDemoCSV = () => {
    const dateMap = {};

    // No existing indexes (e.g. creating the first one): fall back to a sample layout
    const sourceRows = indexNames.length === 0 ? SAMPLE_ROWS : vesselIndexesData;
    const columns = indexNames.length === 0 ? ["SAMPLE_INDEX"] : indexNames;

    sourceRows.forEach((row) => {
      const dateKey = new Date(row.Date).toISOString().split("T")[0];
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { Date: dateKey };
      }

      columns.forEach((idx) => {
        if (row[idx] !== undefined) {
          dateMap[dateKey][idx] = row[idx];
        }
      });
    });

    const csvRows = Object.values(dateMap);
    const csv = Papa.unparse(csvRows, { header: true });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "vessel_indexes_demo.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Parse CSV with multiple index columns
   */
  const parseCSV = (file) => {
    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const headers = results.meta.fields || [];
          if (!headers.includes("Date") || headers.length < 2) {
            throw new Error(
              "CSV must contain Date and at least one index column."
            );
          }

          const indexCols = headers.filter((h) => h !== "Date");

          const normalized = [];

          results.data.forEach((row) => {
            const isoDate = normalizeDateToISO(row.Date);

            if (!isoDate) return;

            indexCols.forEach((idx) => {
              if (row[idx] !== undefined && row[idx] !== "") {
                normalized.push({
                  Date: isoDate,
                  [idx]: Number(row[idx]) || 0,
                });
              }
            });
          });

          onUpload(normalized);
          setParseError(null);
        } catch (err) {
          setParseError(err.message);
        }
      },
      error: () => {
        setParseError("Failed to read CSV file.");
      },
    });
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      parseCSV(f);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type === "text/csv") {
      setFile(f);
      parseCSV(f);
    } else {
      setParseError("Please upload a valid CSV file.");
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setParseError(null);
    onUpload([]);
  };

  return (
    <>
<>
        <button
          type="button"
          onClick={() => setOpenDemoInfo(true)}
          className="h-[24px] px-2.5 mb-2 bg-white hover:bg-gray-50 border border-slate-300 text-slate-700 text-[11px] font-medium rounded-none flex items-center justify-center cursor-pointer whitespace-nowrap"
        >
          Download Demo CSV
        </button>
        <CustomAddModal
        open={openDemoInfo}
        handleClose={() => setOpenDemoInfo(false)}
        onClose={() => setOpenDemoInfo(false)}
        title="Demo CSV"
        saveLabel="Download"
        onSubmitConfirm={() => { handleDownloadDemoCSV(); setOpenDemoInfo(false); }}
      >
        <div className="text-[12px] text-slate-700">
          <div className="border border-slate-200 bg-slate-50 px-3 py-3">
            <div className="font-semibold text-slate-900 mb-1">What is this file?</div>
            <p>A sample CSV with a <strong>Date</strong> column followed by one column per index. If you already have indexes it contains their current values; otherwise it contains example rows to show the layout.</p>
          </div>
          <div className="font-semibold text-slate-900 mt-4 mb-2">How to use it</div>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Click <strong>Download</strong> below to save the sample CSV.</li>
            <li>Open it in Excel or any spreadsheet app. Keep the <strong>Date</strong> and index column headings and enter your own dates and values, one row per date.</li>
            <li>Dates can be written as yyyy-MM-dd, dd-MM-yyyy, MM-dd-yyyy, dd/MM/yyyy or MM/dd/yyyy.</li>
            <li>Save the file as <strong>.csv</strong>.</li>
          </ol>
          <div className="font-semibold text-slate-900 mt-4 mb-2">Where can I upload it?</div>
          <ol className="list-decimal pl-5 space-y-2">
            <li>On the Market Curve Analysis page, click <strong>Create New Index</strong> (to start a new index) or <strong>Insert Index Data</strong> (to add values to existing indexes).</li>
            <li>Select the <strong>CSV</strong> option (shown as <strong>CSV Upload</strong> in Insert Index Data) instead of manual entry.</li>
            <li>Drag and drop your CSV onto the upload area, or click the area to select it. The rest of the form can then be completed and submitted.</li>
          </ol>
        </div>
      </CustomAddModal>
      </>

      <div
        className="flex items-center justify-center w-full"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <label className="flex flex-col items-center justify-center w-full min-h-[80px] border border-dashed border-slate-300 rounded-none cursor-pointer bg-slate-50 hover:bg-slate-100 focus-within:outline focus-within:outline-2 focus-within:outline-[#313C4C] transition-colors">
          <div className="flex flex-col items-center justify-center py-1">
            <CloudUpload style={{ fontSize: "20px" }} className="text-slate-400 mb-0.5" />

            <p className="text-[11px] font-medium text-slate-600 text-center px-3">
              {file ? "Choose a replacement CSV" : "Drag & drop CSV or click to upload"}
            </p>
          </div>

          <input
            type="file"
            accept=".csv"
            className="sr-only"
            aria-label="Upload index CSV"
            onChange={handleFileChange}
          />
        </label>
      </div>
      {file && (
        <div className="flex items-center justify-between gap-2 mt-2 px-2 py-1 border border-slate-200 bg-slate-50 text-[11px]">
          <span className="min-w-0 break-all text-slate-700">{file.name}</span>
          <button type="button" onClick={handleRemoveFile} className="shrink-0 h-7 px-2 text-rose-700 hover:bg-rose-50 transition-colors">
            Remove
          </button>
        </div>
      )}
      {parseError && (
        <div role="alert" className="mt-2 text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1 rounded-none">
          {parseError}
        </div>
      )}
    </>
  );
};

export default CSVUploadModal;
